import React, { useState, useMemo } from 'react';
import { updateBookingStatus, updatePaymentStatus, fetchBookingDetails, deleteBooking } from '../services/bookingService';
import BookingDetailsModal from './BookingDetailsModal';
import MonthSelector from './MonthSelector';
import { exportBookingsToExcel } from '../utils/excelExport';
import '../styles/BookingsTable.css';
import '../styles/AddPackageModal.css';

const BookingsTable = ({ bookings, onUpdate }) => {
  const [updating, setUpdating] = useState({});
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchReference, setSearchReference] = useState('');
  const [searchPackageName, setSearchPackageName] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [exporting, setExporting] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleStatusChange = async (reference, newStatus) => {
    const key = `status-${reference}`;
    setUpdating({ ...updating, [key]: true });
    
    try {
      await updateBookingStatus(reference, newStatus);
      onUpdate(); // Refresh the bookings list
    } catch (error) {
      alert(`Failed to update status: ${error.message}`);
    } finally {
      setUpdating({ ...updating, [key]: false });
    }
  };

  const handlePaymentStatusChange = async (reference, newPaymentStatus) => {
    const key = `payment-${reference}`;
    setUpdating({ ...updating, [key]: true });
    
    try {
      await updatePaymentStatus(reference, newPaymentStatus);
      onUpdate(); // Refresh the bookings list
    } catch (error) {
      alert(`Failed to update payment status: ${error.message}`);
    } finally {
      setUpdating({ ...updating, [key]: false });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('sv-SE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price) + ' SEK';
  };

  // Helper function to clean and capitalize departure city name
  const formatDepartureCity = (city) => {
    if (!city) return '-';
    // Remove package ID suffix (e.g., "-4") and trim
    const cleaned = city.replace(/-\d+$/, '').trim();
    if (!cleaned) return '-';
    // Capitalize first letter, lowercase the rest
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      booking_made: 'badge-booking-made',
      invoice_sent: 'badge-invoice-sent',
      canceled: 'badge-canceled',
      paid: 'badge-paid',
    };
    return classes[status] || 'badge-default';
  };

  const getPaymentStatusBadgeClass = (status) => {
    const classes = {
      pending: 'badge-pending',
      unpaid: 'badge-unpaid',
      paid: 'badge-paid',
      refunded: 'badge-refunded',
    };
    return classes[status] || 'badge-default';
  };

  const handleViewDetails = async (booking) => {
    setLoadingDetails(true);
    try {
      // Fetch full booking details including passengers and tickets
      const fullDetails = await fetchBookingDetails(booking.booking_reference);
      setSelectedBooking(fullDetails);
    } catch (error) {
      alert(`Failed to load booking details: ${error.message}`);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedBooking(null);
  };

  const handleDeleteBooking = async () => {
    if (!bookingToDelete) return;

    try {
      setDeleting(true);
      await deleteBooking(bookingToDelete.booking_reference);
      setBookingToDelete(null);
      onUpdate(); // Refresh the bookings list
    } catch (error) {
      alert(`Failed to delete booking: ${error.message}`);
      console.error('Error deleting booking:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setBookingToDelete(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const filteredAndSortedBookings = useMemo(() => {
    let filtered = [...bookings];

    // Filter by reference
    if (searchReference) {
      filtered = filtered.filter(booking =>
        booking.booking_reference.toLowerCase().includes(searchReference.toLowerCase())
      );
    }

    // Filter by package name
    if (searchPackageName) {
      filtered = filtered.filter(booking => {
        const packageName = booking.package_name || '';
        return packageName.toLowerCase().includes(searchPackageName.toLowerCase());
      });
    }

    // Filter by month
    if (selectedMonth) {
      filtered = filtered.filter(booking => {
        if (!booking.month) return false;
        const [selectedYear, selectedMonthNum] = selectedMonth.split('-');
        const bookingMonth = String(booking.month).toLowerCase();
        
        // Try to match month name or number
        const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
          'july', 'august', 'september', 'october', 'november', 'december'];
        const monthShortNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
          'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        
        // Check if booking month contains the selected month name
        const monthIndex = monthNames.findIndex(m => bookingMonth.includes(m));
        const monthShortIndex = monthShortNames.findIndex(m => bookingMonth.includes(m));
        const foundIndex = monthIndex !== -1 ? monthIndex : (monthShortIndex !== -1 ? monthShortIndex : -1);
        
        if (foundIndex !== -1) {
          // Month name found, check if year matches (if year is in booking.month)
          const bookingYear = bookingMonth.match(/\d{4}/);
          if (bookingYear) {
            return bookingYear[0] === selectedYear && (foundIndex + 1) === parseInt(selectedMonthNum);
          }
          // If no year in booking.month, just match the month
          return (foundIndex + 1) === parseInt(selectedMonthNum);
        }
        
        // Try numeric format (MM or YYYY-MM)
        if (bookingMonth.match(/^\d{1,2}$/)) {
          return parseInt(bookingMonth) === parseInt(selectedMonthNum);
        }
        if (bookingMonth.match(/^\d{4}-\d{1,2}/)) {
          return bookingMonth.startsWith(selectedMonth);
        }
        
        return false;
      });
    }

    // Sort
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle different data types
        if (sortField === 'total_price') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else if (sortField === 'created_at') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        } else {
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [bookings, searchReference, searchPackageName, selectedMonth, sortField, sortOrder]);

  const handleExportFilteredBookings = async () => {
    if (filteredAndSortedBookings.length === 0) {
      alert('No bookings to export');
      return;
    }

    try {
      setExporting(true);
      // Fetch full booking details for filtered bookings
      const bookingsWithDetails = await Promise.all(
        filteredAndSortedBookings.map(async (booking) => {
          try {
            const details = await fetchBookingDetails(booking.booking_reference);
            // Ensure package_name is included
            if (!details.package_name && booking.package_name) {
              details.package_name = booking.package_name;
            }
            return details;
          } catch (error) {
            console.error(`Failed to fetch details for ${booking.booking_reference}:`, error);
            // Fallback to basic booking data with passengers if available
            return booking;
          }
        })
      );
      
      // Determine package name if filtering by package
      const packageName = searchPackageName && filteredAndSortedBookings.length > 0 
        ? filteredAndSortedBookings[0].package_name 
        : null;
      
      // Use the existing export function but with filtered data
      await exportBookingsToExcel(bookingsWithDetails, packageName);
    } catch (error) {
      console.error('Error exporting bookings:', error);
      alert(`Failed to export bookings: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (bookings.length === 0) {
    return (
      <div className="no-bookings">
        <p>No bookings found.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bookings-table-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="search-package-name">Search by Package Name</label>
            <input
              id="search-package-name"
              type="text"
              placeholder="Enter package name..."
              value={searchPackageName}
              onChange={(e) => setSearchPackageName(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="search-reference">Search by Reference</label>
            <input
              id="search-reference"
              type="text"
              placeholder="Enter booking reference..."
              value={searchReference}
              onChange={(e) => setSearchReference(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <MonthSelector
              id="month-selector"
              label="Filter by Month"
              value={selectedMonth}
              onChange={setSelectedMonth}
              placeholder="Select Month"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="sort-select">Sort By</label>
            <select
              id="sort-select"
              value={`${sortField}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortField(field);
                setSortOrder(order);
              }}
              className="filter-select"
            >
              <option value="">No Sorting</option>
              <option value="created_at-desc">Date (Newest First)</option>
              <option value="created_at-asc">Date (Oldest First)</option>
            </select>
          </div>
          {filteredAndSortedBookings.length > 0 && (
            <div className="filter-group">
              <button
                type="button"
                onClick={handleExportFilteredBookings}
                disabled={exporting}
                className="export-button"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = '#059669';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = '#10b981';
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </button>
            </div>
          )}
          {(searchReference || searchPackageName || selectedMonth) && (
            <div className="filter-group">
              <button
                type="button"
                onClick={() => {
                  setSearchReference('');
                  setSearchPackageName('');
                  setSelectedMonth('');
                }}
                className="filter-clear-btn"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="bookings-table-container">
        <table className="bookings-table">
          <thead>
            <tr>
              <th 
                className={sortField === 'booking_reference' ? `sortable sort-${sortOrder}` : 'sortable'}
                onClick={() => handleSort('booking_reference')}
              >
                Reference
                {sortField === 'booking_reference' && (
                  <span className="sort-indicator">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>
                )}
              </th>
              <th>Package Name</th>
              <th>Departure City</th>
              <th 
                className={sortField === 'total_price' ? `sortable sort-${sortOrder}` : 'sortable'}
                onClick={() => handleSort('total_price')}
              >
                Total Price
                {sortField === 'total_price' && (
                  <span className="sort-indicator">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>
                )}
              </th>
              <th>Status</th>
              <th>Payment Status</th>
              <th 
                className={sortField === 'created_at' ? `sortable sort-${sortOrder}` : 'sortable'}
                onClick={() => handleSort('created_at')}
              >
                Created At
                {sortField === 'created_at' && (
                  <span className="sort-indicator">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>
                )}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedBookings.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-results">
                  No bookings match your filters.
                </td>
              </tr>
            ) : (
              filteredAndSortedBookings.map((booking) => (
              <tr key={booking.id}>
                <td className="reference-cell">{booking.booking_reference}</td>
                <td>{booking.package_name || booking.month || '-'}</td>
                <td>{formatDepartureCity(booking.departure_city)}</td>
                <td className="price-cell">{formatPrice(booking.total_price)}</td>
                <td>
                  <select
                    value={booking.status}
                    onChange={(e) => handleStatusChange(booking.booking_reference, e.target.value)}
                    disabled={updating[`status-${booking.booking_reference}`]}
                    className={`status-select ${getStatusBadgeClass(booking.status)}`}
                  >
                    <option value="booking_made">Booking Made</option>
                    <option value="invoice_sent">Invoice Sent</option>
                    <option value="canceled">Canceled</option>
                    <option value="paid">Paid</option>
                  </select>
                </td>
                <td>
                  <select
                    value={booking.payment_status}
                    onChange={(e) => handlePaymentStatusChange(booking.booking_reference, e.target.value)}
                    disabled={updating[`payment-${booking.booking_reference}`]}
                    className={`status-select ${getPaymentStatusBadgeClass(booking.payment_status)}`}
                  >
                    <option value="pending">Pending</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </td>
                <td>{formatDate(booking.created_at)}</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => handleViewDetails(booking)}
                      disabled={loadingDetails}
                      className="view-details-button"
                      title="View full booking details"
                    >
                      {loadingDetails && selectedBooking?.booking_reference === booking.booking_reference
                        ? 'Loading...'
                        : 'View Details'}
                    </button>
                    <div className="icon-button-wrapper">
                      <button
                        onClick={() => setBookingToDelete(booking)}
                        disabled={deleting}
                        title="Delete booking"
                        style={{
                          padding: '8px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '36px',
                          height: '36px',
                          transition: 'background-color 0.2s',
                          opacity: deleting ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!e.currentTarget.disabled) {
                            e.currentTarget.style.backgroundColor = '#dc2626';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!e.currentTarget.disabled) {
                            e.currentTarget.style.backgroundColor = '#ef4444';
                          }
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                      <span className="icon-tooltip">Delete</span>
                    </div>
                  </div>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {selectedBooking && (
        <BookingDetailsModal booking={selectedBooking} onClose={handleCloseModal} />
      )}

      {/* Delete Confirmation Modal */}
      {bookingToDelete && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Booking</h2>
              <button className="close-button" onClick={handleDeleteCancel}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '30px' }}>
              <p style={{ marginBottom: '20px', fontSize: '16px', color: '#374151' }}>
                Are you sure you want to delete the booking <strong>"{bookingToDelete.booking_reference}"</strong>?
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', color: '#6b7280' }}>
                This action cannot be undone. All associated data including passengers and tickets will be permanently deleted.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '30px' }}>
                <button
                  onClick={handleDeleteCancel}
                  className="cancel-button-modal"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteBooking}
                  className="delete-button-modal"
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Booking'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BookingsTable;
